import type { JobCardData } from "../JobCard";
import type { ReactNode } from "react";

import { useLogFeatureDisplay } from "../../../../homepage/logging/DisplayLog";
import { Box, Tag, TagDot } from "../../../../mads";
import { useI18n } from "../../../../shared/I18n";

import { colors } from "@indeed/ifl-tokens";
import { spacing } from "@indeed/ifl-tokens/react-native";
import { Platform } from "react-native";

export enum FlairOption {
  NEW = "NEW",
  URGENTLY_HIRING = "URGENTLY_HIRING",
  HIRING_MULTIPLE_CANDIDATES = "HIRING_MULTIPLE_CANDIDATES",
  RESPONSIVE = "RESPONSIVE",
  EASILY_APPLY = "EASILY_APPLY",
  APPLICATION_REQUESTED = "APPLICATION_REQUESTED",
  RELOCATION_ASSISTANCE = "RELOCATION_ASSISTANCE",
  INTERVIEW_NOW = "INTERVIEW_NOW",
}

export type FlairTagsProps = {
  job: JobCardData;
  hardCodedFlairOptions?: FlairOption[];
};

const EMPLOYER_RESPONSE_TIME_THRESHOLD_DAYS = 14;
const EMPLOYER_RESPONSE_RATE_THRESHOLD = 0.5;
const RELOCATION_ASSISTANCE_BENEFIT_KEY = "EQH9J";
const NEW_JOB_THRESHOLD_MILLISECONDS = 8 * 24 * 60 * 60 * 1000; // 8 days
const MAX_VISIBLE_FLAIRS = 2;

const isResponsiveEmployer = (job: JobCardData): boolean => {
  const { responseRate, averageResponseTimeDays } = job.source?.responsiveness ?? {};
  return (
    (responseRate ?? 0) > EMPLOYER_RESPONSE_RATE_THRESHOLD &&
    !!averageResponseTimeDays &&
    averageResponseTimeDays < EMPLOYER_RESPONSE_TIME_THRESHOLD_DAYS
  );
};

const isRelocationAssistanceJob = (job: JobCardData): boolean => {
  return !!job.benefits?.some((attribute) => attribute.key === RELOCATION_ASSISTANCE_BENEFIT_KEY);
};

const isNewJob = (job: JobCardData): boolean =>
  Math.abs(Date.now() - new Date(Number(job.dateOnIndeed)).getTime()) <=
  NEW_JOB_THRESHOLD_MILLISECONDS;

export const getVisibleFlairOptions = (
  job: JobCardData,
  hardCodedFlairOptions: FlairOption[] = [],
): FlairOption[] => {
  const isFeaturedCard =
    (job.jobPackagingFeatures?.isFeaturedJobCard || job.featuredCardData?.eligible) ?? false;

  const flairOptions: FlairOption[] = [...hardCodedFlairOptions];

  if (job.interviewNowModel) {
    flairOptions.push(FlairOption.INTERVIEW_NOW);
  }

  if (isRelocationAssistanceJob(job)) {
    flairOptions.push(FlairOption.RELOCATION_ASSISTANCE);
  }

  if (job.indeedApply.scopes.includes("MOBILE")) {
    flairOptions.push(FlairOption.EASILY_APPLY);
  }

  if (isNewJob(job)) {
    flairOptions.push(FlairOption.NEW);
  }

  if (job.hiringDemand?.isUrgentHire) {
    flairOptions.push(FlairOption.URGENTLY_HIRING);
  } else if (job.hiringDemand?.isHighVolumeHiring) {
    flairOptions.push(FlairOption.HIRING_MULTIPLE_CANDIDATES);
  } else if (isResponsiveEmployer(job) && !isFeaturedCard) {
    flairOptions.push(FlairOption.RESPONSIVE);
  }

  return flairOptions.slice(0, MAX_VISIBLE_FLAIRS);
};

const useFlairDisplayText = () => {
  const i18n = useI18n();

  return (option: FlairOption, averageResponseTimeDays?: number | null): string => {
    switch (option) {
      case FlairOption.NEW:
        return i18n.gettext("New");
      case FlairOption.URGENTLY_HIRING:
        return i18n.gettext("Urgently hiring");
      case FlairOption.HIRING_MULTIPLE_CANDIDATES:
        return i18n.gettext("Multiple openings");
      case FlairOption.RESPONSIVE:
        return i18n.ngettext(
          "Often replies in {0} day",
          "Often replies in {0} days",
          averageResponseTimeDays ?? 0,
          (averageResponseTimeDays ?? 0).toString(),
        );
      case FlairOption.EASILY_APPLY:
        return i18n.gettext("Easily apply");
      case FlairOption.APPLICATION_REQUESTED:
        return i18n.gettext("Application requested");
      case FlairOption.RELOCATION_ASSISTANCE:
        return i18n.gettext("Relocation assistance");
      case FlairOption.INTERVIEW_NOW:
        return i18n.gettext("Recruiter online now");
      default:
        return "";
    }
  };
};

export const useFlairAccessibilityLabel = (job: JobCardData): string => {
  const flairDisplayText = useFlairDisplayText();

  return getVisibleFlairOptions(job)
    .map((option) => flairDisplayText(option, job.source?.responsiveness?.averageResponseTimeDays))
    .join(", ");
};

export function FlairTags({ job, hardCodedFlairOptions = [] }: FlairTagsProps) {
  const logFeatureDisplay = useLogFeatureDisplay();
  const flairDisplayText = useFlairDisplayText();

  logFeatureDisplay({
    jobKey: job.key,
    field: "newJobTagYN",
    value: isNewJob(job),
  });

  const displayOptions = getVisibleFlairOptions(job, hardCodedFlairOptions);

  // Logged once at the parent level (not per FlairTag) because the display-log
  // buffer is keyed by field name and last-write-wins — a per-tag call would be
  // clobbered by any flair rendered after the Interview Now tag. Reflects what
  // is actually shown after the slice, not merely whether the model is present.
  logFeatureDisplay({
    jobKey: job.key,
    field: "isInterviewNowEnticerShownYN",
    value: displayOptions.includes(FlairOption.INTERVIEW_NOW),
  });

  if (displayOptions.length === 0) {
    return null;
  }

  const accessibilityLabel = displayOptions
    .map((option) => flairDisplayText(option, job.source?.responsiveness?.averageResponseTimeDays))
    .join(", ");

  return (
    <Box
      {...Platform.select({
        ios: { accessible: true },
        android: { screenReaderFocusable: true },
      })}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="text"
      sx={{
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.s1,
        marginBottom: 4,
        flexWrap: "wrap",
      }}
    >
      {displayOptions.map((option, index) => (
        <FlairTag key={`${option}-${index}`} jobKey={job.key} flairOption={option}>
          {flairDisplayText(option, job.source?.responsiveness?.averageResponseTimeDays)}
        </FlairTag>
      ))}
    </Box>
  );
}

const FlairTag = ({
  children,
  jobKey,
  flairOption,
}: {
  children: ReactNode;
  jobKey: string;
  flairOption: FlairOption;
}) => {
  const logFeatureDisplay = useLogFeatureDisplay();

  logFeatureDisplay({
    jobKey,
    field: "flair",
    value: flairOption,
  });
  logFeatureDisplay({
    jobKey,
    field: "is_urgently_hiring_eligible",
    value: flairOption === FlairOption.URGENTLY_HIRING,
  });
  logFeatureDisplay({
    jobKey,
    field: "easyApplyBadgeYN",
    value: flairOption === FlairOption.EASILY_APPLY,
  });

  return (
    <Tag
      status={getTagStatus(flairOption)}
      variant={flairOption === FlairOption.INTERVIEW_NOW ? "solid" : "light"}
      icon={
        flairOption === FlairOption.INTERVIEW_NOW ? (
          <TagDot status="success" variant="solid" />
        ) : undefined
      }
      sx={{
        paddingHorizontal: spacing.scb2,
        ...getTagStyle(flairOption),
      }}
    >
      {children}
    </Tag>
  );
};

const getTagStatus = (flairOption: FlairOption) => {
  if (flairOption === FlairOption.INTERVIEW_NOW) {
    return "success";
  }
  return flairOption === FlairOption.EASILY_APPLY ? "info" : "neutral";
};

const getTagStyle = (flairOption: FlairOption) => {
  switch (flairOption) {
    case FlairOption.INTERVIEW_NOW:
      return {};
    case FlairOption.EASILY_APPLY:
      // Use info style
      return {};
    case FlairOption.RESPONSIVE:
    case FlairOption.RELOCATION_ASSISTANCE:
      return {
        backgroundColor: colors.expressive.purple["200"],
        color: colors.expressive.purple["800"],
      };
    default:
      return {
        backgroundColor: colors.expressive.magenta["200"],
        color: colors.expressive.magenta["800"],
      };
  }
};
